import React, { useState, useEffect, useCallback } from 'react';
import { View, Modal, Alert, Platform } from 'react-native';
import { Text } from '@constants/Themed';
import { XStack, YStack, Card, Button } from 'tamagui';
import { Ionicons, MaterialIcons, FontAwesome5, Entypo } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { HIDE_ALL_MODALS, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import * as Location from 'expo-location';

interface NetworkingSettingsModalProps {
  visible: boolean;
  onClose: () => void;
  user: any;
  company: any;
}

const NetworkingSettingsModal: React.FC<NetworkingSettingsModalProps> = ({ 
  visible, 
  onClose, 
  user, 
  company 
}) => {
  const dispatch = useDispatch();
  const theme = themeSelector();
  const [currentLocation, setCurrentLocation] = useState<any>(null);
  const [locationLoading, setLocationLoading] = useState(false);

  // Get networking schema
  const { data: schemaData, isLoading: schemaLoading } = useQuery({
    queryKey: ['networkingSchema'],
    queryFn: async () => {
      const response = await apis.getAddNetworkSchema();
      console.log('Schema response:', response); // Debug log
      return response;
    },
    retry: 3,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Get user's current location
  const getCurrentLocation = useCallback(async () => {
    setLocationLoading(true);
    try {
      // Request location permissions
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          'Permission Denied',
          'Location permission is required to register your business location.'
        );
        setLocationLoading(false);
        return;
      }

      // Get current position
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.High,
      });

      // Reverse geocode to get address
      const address = await Location.reverseGeocodeAsync({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      });

      const formattedAddress = address[0] ? 
        `${address[0].street || ''} ${address[0].city || ''}, ${address[0].region || ''} ${address[0].postalCode || ''}`.trim() :
        'Unknown location';

      setCurrentLocation({
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        formattedAddress,
        city: address[0]?.city || '',
        region: address[0]?.region || '',
        country: address[0]?.country || '',
        postalCode: address[0]?.postalCode || '',
      });

    } catch (error) {
      console.error('Error getting location:', error);
      Alert.alert(
        'Location Error',
        'Unable to get your current location. Please try again or enter your location manually.'
      );
    } finally {
      setLocationLoading(false);
    }
  }, []);

  // Submit networking information
  const handleSubmitNetworkingInfo = useCallback(async (formData: any) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL, payload: 'Registering your business location...' });

      // Prepare location data
      const locationData = {
        ...formData,
        latitude: currentLocation?.latitude || formData.latitude,
        longitude: currentLocation?.longitude || formData.longitude,
        formattedAddress: currentLocation?.formattedAddress || formData.address,
        city: currentLocation?.city || formData.city,
        region: currentLocation?.region || formData.region,
        country: currentLocation?.country || formData.country,
        postalCode: currentLocation?.postalCode || formData.postalCode,
        companyId: company?._id,
        userId: user?._id,
      };

      // Call API to save networking location
      await apis.addLocation(locationData);

      Alert.alert(
        'Success!',
        'Your business location and services have been registered successfully. You should now appear on the networking map.',
        [{ text: 'OK', onPress: onClose }]
      );

    } catch (error) {
      console.error('Error submitting networking info:', error);
      Alert.alert(
        'Error',
        'Failed to register your business location. Please try again.'
      );
    } finally {
      dispatch({ type: HIDE_ALL_MODALS });
    }
  }, [currentLocation, company, user, dispatch, onClose]);

  // Pre-fill form with current location when available
  const getInitialData = useCallback(() => {
    if (!currentLocation || !schemaData) return undefined;
    
    return {
      latitude: currentLocation.latitude,
      longitude: currentLocation.longitude,
      address: currentLocation.formattedAddress,
      city: currentLocation.city,
      region: currentLocation.region,
      country: currentLocation.country,
      postalCode: currentLocation.postalCode,
    };
  }, [currentLocation, schemaData]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={{
        flex: 1,
        backgroundColor: theme ? 'white' : '#1a1a1a',
      }}>
        {/* Header */}
        <XStack 
          alignItems="center" 
          justifyContent="space-between" 
          paddingHorizontal={20} 
          paddingVertical={16}
          borderBottomWidth={1}
          borderBottomColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
        >
          <XStack alignItems="center" gap={12}>
            <View style={{
              width: 40,
              height: 40,
              backgroundColor: 'rgb(90, 192, 234)',
              borderRadius: 20,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <MaterialIcons name="business" size={20} color="white" />
            </View>
            <YStack>
              <Text style={{ 
                fontSize: 18, 
                fontWeight: 'bold',
                color: theme ? 'black' : 'white'
              }}>
                Networking Settings
              </Text>
              <Text style={{ 
                fontSize: 14, 
                color: theme ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)'
              }}>
                Register your location and services
              </Text>
            </YStack>
          </XStack>
          
          <Button 
            size="$3" 
            circular 
            onPress={onClose}
            backgroundColor="transparent"
            borderWidth={0}
          >
            <Ionicons 
              name="close" 
              size={24} 
              color={theme ? 'black' : 'white'} 
            />
          </Button>
        </XStack>

        {/* Content */}
        <View style={{ flex: 1, padding: 20 }}>
          {/* Info Card */}
          <Card
            padding={16}
            marginBottom={20}
            backgroundColor={theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)'}
            borderWidth={1}
            borderColor="rgba(90, 192, 234, 0.3)"
            borderRadius={12}
          >
            <XStack alignItems="center" gap={12}>
              <Ionicons 
                name="information-circle" 
                size={24} 
                color="rgb(90, 192, 234)" 
              />
              <YStack flex={1}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: theme ? 'black' : 'white',
                  marginBottom: 4
                }}>
                  Join the SmartSaaS Network
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: theme ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
                  lineHeight: 16
                }}>
                  Register your business location and services to appear on the networking map and connect with other businesses in your area.
                </Text>
              </YStack>
            </XStack>
          </Card>

          {/* Location Button */}
          <Card
            padding={16}
            marginBottom={20}
            backgroundColor={theme ? 'white' : 'rgba(40, 40, 40, 0.95)'}
            borderWidth={1}
            borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
            borderRadius={12}
          >
            <XStack alignItems="center" justifyContent="space-between">
              <YStack flex={1}>
                <Text style={{
                  fontSize: 16,
                  fontWeight: '600',
                  color: theme ? 'black' : 'white',
                  marginBottom: 4
                }}>
                  Current Location
                </Text>
                <Text style={{
                  fontSize: 14,
                  color: theme ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)'
                }}>
                  {currentLocation ? currentLocation.formattedAddress : 'Not detected'}
                </Text>
              </YStack>
              
              <Button
                size="$3"
                backgroundColor="rgb(90, 192, 234)"
                onPress={getCurrentLocation}
                disabled={locationLoading}
                borderRadius={8}
                paddingHorizontal={16}
              >
                <XStack alignItems="center" gap={8}>
                  <Ionicons 
                    name="location" 
                    size={16} 
                    color="white" 
                  />
                  <Text style={{ color: 'white', fontWeight: '500' }}>
                    {locationLoading ? 'Getting...' : 'Detect'}
                  </Text>
                </XStack>
              </Button>
            </XStack>
          </Card>

          {/* Form */}
          {!schemaLoading && schemaData ? (
            <Card
              flex={1}
              padding={20}
              backgroundColor={theme ? 'white' : 'rgba(40, 40, 40, 0.95)'}
              borderWidth={1}
              borderColor={theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}
              borderRadius={12}
            >
              <GenerateSchemaForm
                noBackground
                hideLabel={false}
                nullify={['store','location']}
                Progress={false}
                schema={schemaData?.schema || schemaData}
                containerWidth={undefined}
                scrollEnabled={true}
                avoidKeyboardDisabled={false}
                customStyling={{
                  flex: 1,
                  paddingBottom: 10,
                }}
                buttonStyle={{
                  alignSelf: 'center',
                  minHeight: 48,
                  borderColor: 'rgb(90, 192, 234)',
                  backgroundColor: 'rgb(90, 192, 234)',
                  marginTop: 24,
                  borderRadius: 12,
                  paddingHorizontal: 32,
                  shadowColor: '#000',
                  shadowOffset: { width: 0, height: 2 },
                  shadowOpacity: 0.15,
                  shadowRadius: 8,
                  elevation: 3,
                }}
                submitFunction={handleSubmitNetworkingInfo}
                buttonLabel="Register on Network"
                retainInitialValues={false}
                initialData={getInitialData()}
              />
            </Card>
          ) : (
            <View style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Text style={{
                color: theme ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
                fontSize: 16
              }}>
                Loading networking form...
              </Text>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

export default NetworkingSettingsModal;
